/**
 * portTable -- the pure logic behind the site-wide switch PORT TABLE (routes/SwitchPorts.tsx)
 * and its bulk editor (components/site/BulkPortEditDialog.tsx).
 *
 * WHY THIS EXISTS (owner, 2026-09-14)
 * "it is not easy to select multiple ports and name them and or edit them", next to a Meraki
 * "Switch Ports" screenshot: one row per port across the site, a checkbox per row, a filter box,
 * "14 of 3488 switch ports", and one Edit dialog for the whole selection. Meridian had a
 * per-switch card list and a 28-tile faceplate where multi-select meant ctrl-click.
 *
 * Everything here is a pure function (no React, no fetch) so the rules an operator relies on --
 * which ports a pattern names what, which rows a shift-click takes, which commands go to which
 * switch, which ports need a loop warning, what a URL filter means -- are pinned by
 * `npm run test:unit` (src/lib/portTable.test.ts).
 *
 * NAMES ARE NOT CLI CHANGES (owner decision 2026-09-14, option C "Meraki auto-sync")
 * Meridian is the source of truth for a port's name. A name is saved immediately through the
 * name API and the controller writes it to the switch `description` in the background (one CLI
 * session per switch, then save) -- see lib/portNameSync. So buildChangeGroups NEVER puts a
 * `name` into a port change: the per-switch preview carries VLAN / PoE / admin / STP / 802.1X /
 * LLDP only, and a name is never sent to a switch twice. The bulk Name field's pattern rules
 * (`expandNamePattern`, `sanitizePortName`) still live here and are shared with portNameSync.
 */
import { formatPortRefs, formatSwitchPort, isUcentralPortName } from "./switchPorts.ts";
import { portCarries, type PortVlanView } from "./portVlans.ts";
import type { PortChange, SwitchPortDetail } from "@/lib/api/hooks";

// =========================================================================================
// rows
// =========================================================================================

/** The switch a group of rows belongs to (a subset of the device-list Device). */
export interface PortTableSwitch {
  serial: string;
  name: string;
  model?: string | null;
  online: boolean;
  /** The device's known uplink port (Device.uplink.port), e.g. "Ethernet27". */
  uplinkPort?: string | null;
}

/** A Meridian device an LLDP neighbour may resolve to. */
export interface NeighborDevice {
  serial: string;
  name: string;
  kind?: string | null;
  model?: string | null;
}

/** One row of the port table: one physical port on one switch, plus what we derived about it. */
export interface PortRow {
  /** Stable selection key: `${serial}|${port name}`. */
  key: string;
  serial: string;
  switchName: string;
  switchModel: string | null;
  switchOnline: boolean;
  port: SwitchPortDetail;
  /** Operator-facing port number ("28"), 1-based -- see lib/switchPorts. */
  number: string;
  /** Faceplate order inside its switch. */
  order: number;
  isUplink: boolean;
  /** The neighbour's system part ("JH-GW-01"), null when LLDP reports nothing. */
  neighborSystem: string | null;
  /** The neighbour's port part ("eth0"), null when not reported. */
  neighborPort: string | null;
  /** The Meridian device the neighbour resolved to, when it resolved unambiguously. */
  neighborDevice: NeighborDevice | null;
  /** True when the neighbour is (or looks like) a switch / gateway / firewall. */
  infraNeighbor: boolean;
  /**
   * The port's VLAN view from GET switch/port-vlans (OP#1819): native, the DEVICE's tagged list
   * when known (`observed`), source and confidence. Absent/null while loading or when the switch
   * has no view -- the VLAN cell then falls back to the port list's native VLAN.
   */
  vlans?: PortVlanView | null;
}

/** Selection key for one port. `|` never appears in a serial. */
export function portKey(serial: string, portName: string): string {
  return `${serial}|${portName}`;
}

/**
 * Split the backend's neighbour label into system + port.
 *
 * services/switch_ports_state._lldp_by_port joins them as `"<who> - <where>"`. The SYSTEM part
 * can itself contain " - " far more plausibly than a remote port id can, so split on the LAST
 * separator. A label with no separator is all system.
 */
export function splitNeighbor(text: string | null | undefined): { system: string | null; port: string | null } {
  const t = (text ?? "").trim();
  if (!t) return { system: null, port: null };
  const at = t.lastIndexOf(" - ");
  if (at < 0) return { system: t, port: null };
  const system = t.slice(0, at).trim();
  const port = t.slice(at + 3).trim();
  return { system: system || null, port: port || null };
}

/**
 * Resolve an LLDP system string to a Meridian device, or null.
 *
 * Exact (case-insensitive) name or serial first. Then MODEL, but only when exactly one device
 * in scope has it: switches that were never given a hostname advertise their model
 * ("ECS4150-28P"), and linking to the one ECS4150 at a site is right while guessing between two
 * is not. Never a substring match -- a wrong link is worse than no link.
 */
export function matchNeighborDevice(
  system: string | null,
  devices: readonly NeighborDevice[],
): NeighborDevice | null {
  if (!system) return null;
  const s = system.trim().toLowerCase();
  const byName = devices.find((d) => d.name.toLowerCase() === s || d.serial.toLowerCase() === s);
  if (byName) return byName;
  const byModel = devices.filter((d) => (d.model ?? "").toLowerCase() === s);
  return byModel.length === 1 ? (byModel[0] ?? null) : null;
}

/** Device kinds whose link a port change can loop or cut (lib/deviceKinds vocabulary). */
export const INFRA_KINDS: ReadonlySet<string> = new Set(["switch", "gateway", "firewall", "wireless_controller"]);

/**
 * Text that marks an LLDP neighbour as network infrastructure when it did not resolve to a
 * Meridian device. DELIBERATELY BROAD: a false positive costs one confirmation click, a false
 * negative can put an edge port on a switch-to-switch link and loop the site. "uCentral" is in
 * the list because the lab gateways advertise it; an AP that also does gets one extra confirm.
 */
const INFRA_TEXT =
  /\b(ecs\d*|ols\d*|switch|gateway|gw|router|firewall|core|idf|mdf|srx\d*|fortigate|pfsense|qotom|ucentral|openwrt|catalyst|nexus|aos-cx)\b|-sw\d+/i;

/** Is this neighbour a switch / gateway / firewall? A resolved device's kind outranks the text. */
export function isInfraNeighbor(system: string | null, device: NeighborDevice | null): boolean {
  if (device?.kind) return INFRA_KINDS.has(device.kind);
  return !!system && INFRA_TEXT.test(system);
}

/**
 * Normalise a port identifier to "unit/port" so the uCentral name ("Ethernet27"), the CLI form
 * ("1/28") and SNMP spellings ("Eth 1/28", "Ethernet1/28") compare equal. Anything else is
 * compared lower-cased.
 */
export function ifaceId(name: string | null | undefined): string {
  const n = (name ?? "").trim();
  if (isUcentralPortName(n)) return formatSwitchPort(n, "cli");
  const m = n.match(/(\d+)\/(\d+)$/);
  return m ? `${m[1]}/${m[2]}` : n.toLowerCase();
}

/** Build the table rows for every switch in scope, ordered by switch name then faceplate. */
export function buildPortRows(
  switches: readonly {
    sw: PortTableSwitch;
    ports: readonly SwitchPortDetail[];
    /** Per-port VLAN views of this switch, keyed by uCentral port name (port-vlans `ports`). */
    vlans?: Readonly<Record<string, PortVlanView>> | null;
  }[],
  devices: readonly NeighborDevice[],
): PortRow[] {
  const rows: PortRow[] = [];
  const ordered = [...switches].sort((a, b) => a.sw.name.localeCompare(b.sw.name, undefined, { numeric: true }));
  for (const { sw, ports, vlans } of ordered) {
    const uplink = sw.uplinkPort ? ifaceId(sw.uplinkPort) : null;
    const sorted = [...ports].sort((a, b) => a.ifindex - b.ifindex);
    sorted.forEach((port, i) => {
      const { system, port: remotePort } = splitNeighbor(port.neighbor);
      const device = matchNeighborDevice(system, devices);
      rows.push({
        key: portKey(sw.serial, port.name),
        serial: sw.serial,
        switchName: sw.name,
        switchModel: sw.model ?? null,
        switchOnline: sw.online,
        port,
        number: isUcentralPortName(port.name) ? formatSwitchPort(port.name) : port.name,
        order: i,
        isUplink: uplink !== null && ifaceId(port.name) === uplink,
        neighborSystem: system,
        neighborPort: remotePort,
        // A switch "neighbouring itself" (model match on its own model) is not a link target.
        neighborDevice: device && device.serial !== sw.serial ? device : null,
        infraNeighbor: isInfraNeighbor(system, device),
        vlans: vlans?.[port.name] ?? null,
      });
    });
  }
  return rows;
}

// =========================================================================================
// cell formatting (never guess: "—" when the switch did not report it)
// =========================================================================================

/** Operational status of a port. `disabled` only when a transport really reported admin-down. */
export function portStatus(port: SwitchPortDetail): "up" | "down" | "disabled" {
  // Strict === false: the uCentral path sends admin_up null (it cannot tell admin-down from
  // link-down), and null must never render as "someone disabled this port".
  if (port.admin_up === false) return "disabled";
  return port.status === "up" ? "up" : "down";
}

/**
 * Native-only VLAN text from the PORT LIST (interfaces[].vlan_id, or the SNMP PVID). The table's
 * VLAN column now renders lib/portVlans.vlanCell (tagged list from the switch when read, OP#1819)
 * and falls back to this when the port-vlans view is not available.
 */
export function formatVlanCell(port: SwitchPortDetail): string {
  return port.native_vlan != null ? String(port.native_vlan) : "—";
}

/** "1G full" / "100M" / "—". Speed only means something while the link is up. */
export function formatLink(port: SwitchPortDetail): string {
  if (port.status !== "up" || !port.speed_mbps) return "—";
  const speed = port.speed_mbps >= 1000 ? `${port.speed_mbps / 1000}G` : `${port.speed_mbps}M`;
  return port.duplex ? `${speed} ${String(port.duplex).toLowerCase()}` : speed;
}

/** PoE cell: real draw when reported, on/off otherwise, "—" when the transport has no PoE data. */
export function formatPoe(port: SwitchPortDetail): string {
  if (port.poe_power_mw && port.poe_power_mw > 0) return `On · ${(port.poe_power_mw / 1000).toFixed(1)} W`;
  if (port.poe_enabled === true) return "On";
  if (port.poe_enabled === false) return "Off";
  return "—";
}

/** Byte counter for the traffic columns. */
export function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  if (n >= 1e3) return `${Math.round(n / 1e3)} KB`;
  return `${n} B`;
}

/** True when any row carries non-zero counters -- the traffic columns only render then. */
export function hasTrafficCounters(rows: readonly PortRow[]): boolean {
  return rows.some((r) => (r.port.tx_bytes ?? 0) > 0 || (r.port.rx_bytes ?? 0) > 0);
}

// =========================================================================================
// URL filters
// =========================================================================================

/** The filter row, as stored in the query string (?q=&switch=&status=&poe=&vlan=). */
export interface PortFilters {
  q: string;
  /** Switch serial. */
  switch: string;
  status: "" | "up" | "down" | "disabled";
  poe: "" | "on" | "off";
  /** A VLAN id, as typed. */
  vlan: string;
}

/** Every key the filter row owns -- "Clear filters" deletes exactly these (plus `ports`). */
export const FILTER_KEYS = ["q", "switch", "status", "poe", "vlan"] as const;

/** Parse the filter row from the URL. Unknown enum values read as "no filter", never as match-nothing. */
export function parsePortFilters(params: URLSearchParams): PortFilters {
  const status = params.get("status") ?? "";
  const poe = params.get("poe") ?? "";
  const vlan = (params.get("vlan") ?? "").trim();
  return {
    q: params.get("q") ?? "",
    switch: params.get("switch") ?? "",
    status: status === "up" || status === "down" || status === "disabled" ? status : "",
    poe: poe === "on" || poe === "off" ? poe : "",
    vlan: /^\d{1,4}$/.test(vlan) ? vlan : "",
  };
}

/** True when any filter narrows the table. */
export function anyPortFilter(f: PortFilters): boolean {
  return !!(f.q.trim() || f.switch || f.status || f.poe || f.vlan);
}

/** Everything a search term is matched against for one row (lower-cased). */
function haystack(r: PortRow): string {
  return [
    r.switchName, r.serial, r.number, r.port.name, r.port.alias, r.port.descr,
    r.port.neighbor, formatPortRefs(r.port.neighbor), r.neighborDevice?.name,
    r.port.native_vlan != null ? `vlan ${r.port.native_vlan}` : null,
    // Tagged VLANs the switch reports (device read): "vlan 1000" finds every trunk carrying it.
    ...(r.vlans?.observed?.tagged ?? []).map((v) => `vlan ${v}`),
    r.isUplink ? "uplink" : null,
  ].filter(Boolean).join("\n").toLowerCase();
}

/**
 * Apply the filter row. Search terms are whitespace-separated and ALL must match (so
 * "sw09 room" narrows), over switch, serial, port number, name, neighbour and "vlan N".
 */
export function filterPortRows(rows: readonly PortRow[], f: PortFilters): PortRow[] {
  const terms = f.q.toLowerCase().split(/\s+/).filter(Boolean);
  const vlan = f.vlan ? Number(f.vlan) : null;
  return rows.filter((r) => {
    if (f.switch && r.serial !== f.switch) return false;
    if (f.status && portStatus(r.port) !== f.status) return false;
    if (f.poe === "on" && r.port.poe_enabled !== true) return false;
    if (f.poe === "off" && r.port.poe_enabled !== false) return false;
    // Native OR (device-read) tagged. An unknown tagged list does not match: the filter shows
    // ports PROVEN to carry the VLAN, never ones that merely might.
    if (vlan !== null && r.port.native_vlan !== vlan && portCarries(r.vlans, vlan) !== true) return false;
    if (terms.length) {
      const h = haystack(r);
      if (!terms.every((t) => h.includes(t))) return false;
    }
    return true;
  });
}

/** `?ports=3,4,8` -> ["3","4","8"]: 1-based faceplate numbers, deduped, junk dropped. */
export function parsePortsParam(value: string | null | undefined): string[] {
  const out: string[] = [];
  for (const tok of (value ?? "").split(",")) {
    const t = tok.trim();
    if (/^\d{1,3}$/.test(t) && Number(t) > 0 && !out.includes(String(Number(t)))) out.push(String(Number(t)));
  }
  return out;
}

/** The row keys a `?switch=&ports=` deep link preselects. */
export function preselectKeys(rows: readonly PortRow[], serial: string, numbers: readonly string[]): string[] {
  return rows.filter((r) => r.serial === serial && numbers.includes(r.number)).map((r) => r.key);
}

/** Deep link into the port table, scoped like the nav (DomainRail): site when one is selected. */
export function portTableHref(opts: {
  orgId: string | null;
  siteId: string | null;
  serial?: string | null;
  portNumbers?: readonly string[];
}): string {
  const base = opts.siteId ? `/o/${opts.orgId}/s/${opts.siteId}/switch-ports` : `/o/${opts.orgId}/switch-ports`;
  const qs = new URLSearchParams();
  if (opts.serial) qs.set("switch", opts.serial);
  if (opts.serial && opts.portNumbers?.length) qs.set("ports", opts.portNumbers.join(","));
  const s = qs.toString();
  // URLSearchParams encodes "," as %2C; keep the list readable in the address bar.
  return s ? `${base}?${s.replace(/%2C/g, ",")}` : base;
}

// =========================================================================================
// selection
// =========================================================================================

/** Toggle one key. Returns a new Set (React state must not be mutated). */
export function toggleKey(prev: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(prev);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/**
 * Checkbox click with the Devices-list semantics: plain click toggles one row; shift-click ADDS
 * every row between the anchor and this one, in the table's CURRENT visual (sorted, filtered)
 * order, so the range is what the operator sees. Without an anchor, shift behaves as a plain click.
 */
export function selectClick(
  prev: ReadonlySet<string>,
  visualKeys: readonly string[],
  anchorIndex: number | null,
  index: number,
  shift: boolean,
): Set<string> {
  const key = visualKeys[index];
  if (key === undefined) return new Set(prev);
  if (shift && anchorIndex !== null && anchorIndex >= 0 && anchorIndex < visualKeys.length) {
    const [lo, hi] = anchorIndex < index ? [anchorIndex, index] : [index, anchorIndex];
    const next = new Set(prev);
    for (const k of visualKeys.slice(lo, hi + 1)) next.add(k);
    return next;
  }
  return toggleKey(prev, key);
}

/** Header checkbox: select every visible row, or -- when all are selected -- unselect them. */
export function toggleAllVisible(prev: ReadonlySet<string>, visibleKeys: readonly string[]): Set<string> {
  const next = new Set(prev);
  const all = visibleKeys.length > 0 && visibleKeys.every((k) => next.has(k));
  for (const k of visibleKeys) {
    if (all) next.delete(k);
    else next.add(k);
  }
  return next;
}

// =========================================================================================
// bulk edit -> per-switch changes
// =========================================================================================

/** Longest label models/switch_port_label stores (api MAX_LABEL). */
export const MAX_PORT_NAME = 128;

/**
 * Mirror of the renderer's description handling (services/switch_portconfig: strip `"`, trim),
 * applied to the label too so the switch and Meridian hold the identical string.
 */
export function sanitizePortName(name: string): string {
  return name.replace(/"/g, "").trim();
}

/**
 * Expand a name pattern for one port. Tokens (case-insensitive):
 *   {port}    the 1-based faceplate number ("Room 10{port}" on port 3 -> "Room 103")
 *   {switch}  the switch's name
 * A pattern without tokens is the same name for every selected port -- that is what the
 * operator typed, so it is not second-guessed (the live preview shows the result).
 */
export function expandNamePattern(pattern: string, ctx: { port: string; switchName: string }): string {
  return sanitizePortName(pattern.replace(/\{port\}/gi, ctx.port).replace(/\{switch\}/gi, ctx.switchName));
}

/** The bulk Edit dialog's form. Every field's empty value means "No change". */
export interface BulkEdit {
  nameMode: "keep" | "set" | "clear";
  namePattern: string;
  enabled: "" | "up" | "down";
  vlanMode: "" | "access" | "trunk";
  accessVlan: string;
  nativeVlan: string;
  /** Trunk: VLANs to ADD tagged (csv, ranges allowed). */
  taggedVlans: string;
  /** Trunk: VLANs to remove (csv, ranges allowed). */
  removeVlans: string;
  poe: "" | "on" | "off";
  edgePort: "" | "on" | "off";
  dot1x: "" | "auto" | "force-authorized" | "force-unauthorized";
  lldp: "" | "both" | "tx" | "rx" | "off";
}

export const EMPTY_EDIT: BulkEdit = {
  nameMode: "keep", namePattern: "", enabled: "", vlanMode: "", accessVlan: "", nativeVlan: "",
  taggedVlans: "", removeVlans: "", poe: "", edgePort: "", dot1x: "", lldp: "",
};

/** Parse "601,651, 1254-1255" into VLAN ids (ranges expanded, deduped, sorted) + the bad tokens. */
export function parseVlanList(csv: string): { vlans: number[]; invalid: string[] } {
  const seen = new Set<number>();
  const invalid: string[] = [];
  for (const tok of csv.split(/[,\s]+/)) {
    const t = tok.trim();
    if (!t) continue;
    const m = t.match(/^(\d+)-(\d+)$/);
    if (m) {
      const a = Number(m[1]);
      const b = Number(m[2]);
      if (a < 1 || b > 4094 || a > b) {
        invalid.push(t);
        continue;
      }
      for (let v = a; v <= b; v++) seen.add(v);
    } else if (/^\d+$/.test(t) && Number(t) >= 1 && Number(t) <= 4094) {
      seen.add(Number(t));
    } else {
      invalid.push(t);
    }
  }
  return { vlans: [...seen].sort((x, y) => x - y), invalid };
}

/** One VLAN id field: the id, or an error message naming the field. Empty -> null id, no error. */
function vlanField(value: string, label: string): { vid: number | null; error: string | null } {
  const t = value.trim();
  if (!t) return { vid: null, error: null };
  if (!/^\d+$/.test(t) || Number(t) < 1 || Number(t) > 4094) {
    return { vid: null, error: `${label}: "${t}" is not a VLAN id (1-4094)` };
  }
  return { vid: Number(t), error: null };
}

/** What will be sent to ONE switch: its port changes and the labels to write after it accepts. */
export interface SwitchChangeGroup {
  serial: string;
  switchName: string;
  changes: PortChange[];
  /** 1-based numbers of the ports in `changes`, for display. */
  portNumbers: string[];
  /** Plain-words notes shown with this switch's preview, e.g. "VLAN 400 stays on port 4, tagged". */
  notes: string[];
}

/**
 * Turn the form + selection into per-switch change sets, or validation errors.
 *
 * Only fields the operator set are emitted, per port. The Name field is ignored here: names save
 * through the name API (lib/portNameSync.buildNameItems), so a name-only edit has no CLI change. VLAN moves carry what the renderer needs to leave exactly one untagged
 * VLAN: the port's CURRENT native goes in `remove_vlans` (access) or `previous_native_vlan`
 * (trunk) when it is known and differs. Without that an ECS hybrid port keeps the old VLAN
 * untagged beside the new one and bridges two broadcast domains (services/switch_portconfig:
 * "the caller passes the outgoing VLAN in remove_vlans").
 */
export function buildChangeGroups(
  rows: readonly PortRow[],
  edit: BulkEdit,
): { groups: SwitchChangeGroup[]; errors: string[] } {
  const errors: string[] = [];
  const none = { vid: null, error: null };

  const access = edit.vlanMode === "access" ? vlanField(edit.accessVlan, "Access VLAN") : none;
  const native = edit.vlanMode === "trunk" ? vlanField(edit.nativeVlan, "Native VLAN") : none;
  if (access.error) errors.push(access.error);
  if (native.error) errors.push(native.error);
  if (edit.vlanMode === "access" && !edit.accessVlan.trim()) errors.push("Access VLAN: required for an access port");
  const emptyList = { vlans: [] as number[], invalid: [] as string[] };
  const tagged = edit.vlanMode === "trunk" ? parseVlanList(edit.taggedVlans) : emptyList;
  const removed = edit.vlanMode === "trunk" ? parseVlanList(edit.removeVlans) : emptyList;
  if (tagged.invalid.length) errors.push(`Tagged VLANs: not a VLAN id: ${tagged.invalid.join(", ")}`);
  if (removed.invalid.length) errors.push(`Remove VLANs: not a VLAN id: ${removed.invalid.join(", ")}`);
  if (
    edit.vlanMode === "trunk" && !edit.nativeVlan.trim() && !edit.taggedVlans.trim() && !edit.removeVlans.trim()
  ) {
    errors.push("Trunk: set a native VLAN, tagged VLANs to add, or VLANs to remove");
  }

  const groups: SwitchChangeGroup[] = [];
  const bySerial = new Map<string, SwitchChangeGroup>();
  const ordered = [...rows].sort(
    (a, b) => a.switchName.localeCompare(b.switchName, undefined, { numeric: true }) || a.order - b.order,
  );

  for (const r of ordered) {
    // No `name` here, ever: names sync to the switch on their own (module header).
    const c: PortChange = { port: r.port.name };
    if (edit.enabled) c.enabled = edit.enabled === "up";
    if (edit.vlanMode === "access" && access.vid !== null) {
      c.mode = "access";
      c.access_vlan = access.vid;
      const current = r.port.native_vlan;
      // VLAN 1 is stripped by the renderer itself for any access VLAN other than 1.
      if (current != null && current !== access.vid && current !== 1) c.remove_vlans = [current];
    }
    const notes: string[] = [];
    if (edit.vlanMode === "trunk" && (native.vid !== null || tagged.vlans.length || removed.vlans.length)) {
      c.mode = "trunk";
      if (native.vid !== null) {
        c.native_vlan = native.vid;
        const current = r.port.native_vlan;
        // MR !137 review (2026-09-14): never hand the renderer the outgoing native for removal
        // when the operator is keeping that VLAN as TAGGED ("native 400 -> native 100, tag 400").
        // The backend filters it too (services/switch_portconfig); this is defence in depth, and
        // the note tells the operator why no removal is rendered for it.
        if (current != null && current !== native.vid) {
          if (tagged.vlans.includes(current)) notes.push(`VLAN ${current} stays on port ${r.number}, tagged`);
          else c.previous_native_vlan = current;
        }
      }
      if (tagged.vlans.length) c.tagged_vlans = tagged.vlans;
      // A VLAN listed both to add tagged and to remove is kept: removing what the same edit adds
      // would silently undo the operator's own request.
      const keptOverlap = removed.vlans.filter((v) => tagged.vlans.includes(v) || v === native.vid);
      const removals = removed.vlans.filter((v) => !keptOverlap.includes(v));
      if (keptOverlap.length) notes.push(`VLAN ${keptOverlap.join(", ")} not removed from port ${r.number}: this edit also adds it`);
      if (removals.length) c.remove_vlans = removals;
    }
    if (edit.poe) c.poe = edit.poe === "on";
    if (edit.edgePort) c.edge_port = edit.edgePort === "on";
    if (edit.dot1x) c.dot1x = edit.dot1x;
    if (edit.lldp) c.lldp = edit.lldp;

    if (Object.keys(c).length === 1) continue; // only `port`: nothing to change on this port

    let g = bySerial.get(r.serial);
    if (!g) {
      g = { serial: r.serial, switchName: r.switchName, changes: [], portNumbers: [], notes: [] };
      bySerial.set(r.serial, g);
      groups.push(g);
    }
    g.changes.push(c);
    g.portNumbers.push(r.number);
    g.notes.push(...notes);
  }

  if (!errors.length && rows.length > 0 && groups.length === 0) {
    errors.push("Nothing to change: every field is No change");
  }
  return { groups: errors.length ? [] : groups, errors: [...new Set(errors)] };
}

/** The identity of a request set. A preview is only valid while the form still renders this. */
export function changeGroupsKey(groups: readonly SwitchChangeGroup[], save: boolean): string {
  return JSON.stringify({ save, groups: groups.map((g) => ({ s: g.serial, c: g.changes })) });
}

// =========================================================================================
// uplink guard
// =========================================================================================

/** Why a row is dangerous to change, or null. */
export function portRisk(r: PortRow): "uplink" | "infra_neighbor" | null {
  if (r.isUplink) return "uplink";
  if (r.infraNeighbor) return "infra_neighbor";
  return null;
}

/** "the switch's uplink" / "connects to JH-GW-01 (gateway)" -- the warning line for one risky row. */
export function describeRisk(r: PortRow): string {
  if (r.isUplink) return "the switch's uplink";
  const who = r.neighborDevice?.name ?? r.neighborSystem ?? "a switch or gateway";
  const kind = r.neighborDevice?.kind ? ` (${r.neighborDevice.kind.replace(/_/g, " ")})` : "";
  return `connects to ${who}${kind}`;
}

/**
 * The edits that can cut or loop a switch-to-switch / gateway link, in plain words.
 * Empty -> no confirmation needed whatever the selection.
 *   disable                    takes the link down
 *   any VLAN change            can strand the management VLAN on the far side
 *   edge port ON               a switch-to-switch edge port can loop the network
 *   802.1X auto / force-unauth a switch does not authenticate: the link stops forwarding
 *   PoE off                    powers off a PoE-fed switch, bridge or gateway
 * Beyond the brief (Disable / VLAN / Edge port): 802.1X and PoE off cut an inter-switch link
 * just as surely, and the cost of including them is one checkbox.
 */
export function guardedEdits(edit: BulkEdit): string[] {
  const out: string[] = [];
  if (edit.enabled === "down") out.push("disable the port");
  if (edit.vlanMode) out.push("change its VLANs");
  if (edit.edgePort === "on") out.push("make it an edge port (can loop the network)");
  if (edit.dot1x === "auto" || edit.dot1x === "force-unauthorized") out.push("enforce 802.1X");
  if (edit.poe === "off") out.push("turn PoE off");
  return out;
}

/** Rows in the selection that need the explicit confirmation for these edits. */
export function rowsNeedingConfirm(rows: readonly PortRow[], edit: BulkEdit): PortRow[] {
  return guardedEdits(edit).length ? rows.filter((r) => portRisk(r) !== null) : [];
}
